import { brandColors } from '@assets/colors/brandColors';
import BookmarkIcon from '@assets/images/common/icon/bookmark/BookmarkIcon';
import HeartIcon from '@assets/images/common/icon/heart/HeartIcon';
import LockIcon from '@assets/images/common/icon/lock/LockIcon';
import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { MediaTag } from '@src/api/types/mediaTag';
import BookmarkLoginPopup from '@src/components/BookmarkLoginPopup';
import FavoriteLoginPopup from '@src/components/FavoriteLoginPopup';
import useIsMobile from '@src/hooks/useIsMobile';
import Tag from '@src/ui/elements/Company/Tag';
import generateUrlWithParams from '@src/utils/generateUrlWithParams';
import { formatLikesCount, stripHtmlTags } from '@src/utils/stringUtils';
import React from 'react';
import { Link } from 'react-router-dom';

interface ArticleItemProps {
  to: string;
  title: string;
  content: string;
  hideMobileDescription?: boolean;
  date: string;
  category: { id: number; name: string; slug: string };
  tags: MediaTag[];
  thumbnail: string;
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  onBookmarkClickWhenLoggedOut?: () => void;
  bookmarkPopupId?: string;
  visiblePopupId?: string | null;
  popupRef?: React.RefObject<HTMLDivElement>;
  onPopupClose?: () => void;
  isLiked?: boolean;
  likesCount?: number;
  onFavoriteToggle?: () => void;
  onFavoriteClickWhenLoggedOut?: () => void;
  favoritePopupId?: string;
  isMemberLimited?: boolean;
  lockIconMdClassName?: string;
}

const ArticleItem: React.FC<ArticleItemProps> = ({
  to,
  title,
  content,
  hideMobileDescription,
  date,
  category,
  tags,
  thumbnail,
  isBookmarked = false,
  onBookmarkToggle,
  onBookmarkClickWhenLoggedOut,
  bookmarkPopupId,
  visiblePopupId,
  popupRef,
  onPopupClose,
  isLiked = false,
  likesCount = 0,
  onFavoriteToggle,
  onFavoriteClickWhenLoggedOut,
  favoritePopupId,
  isMemberLimited,
  lockIconMdClassName = 'md:mt-[3.5px] md:size-[15px]',
}) => {
  const isMobile = useIsMobile();
  const isPopupVisible = bookmarkPopupId !== undefined && visiblePopupId === bookmarkPopupId;
  const isFavoritePopupVisible =
    favoritePopupId !== undefined && visiblePopupId === favoritePopupId;

  return (
    <article className='md:relative'>
      <div className='flex flex-col gap-3 transition-opacity hover:opacity-80 md:flex-row'>
        <Link
          to={to}
          className='block md:w-[44%]'
        >
          <img
            className='aspect-[360/202] size-full object-contain'
            src={thumbnail}
            alt={title}
            width={360}
            height={202}
            loading='lazy'
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = PhotoGrayIcon;
            }}
          />
        </Link>
        <div className='flex flex-col justify-between md:ml-1 md:w-[52%]'>
          <div>
            <div className='mb-2 flex items-center justify-between'>
              <Link
                to={generateUrlWithParams('/articles', {
                  category_slug: category.slug,
                })}
                className='max-w-[70%] cursor-pointer truncate border-b-2 border-primary-500 py-1 text-xs text-primary'
              >
                {category.name}
              </Link>
              <div className='flex items-center gap-2'>
                <time
                  className='text-[10px] text-gray-400'
                  dateTime={date}
                >
                  {date}
                </time>
                <div className='relative'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onFavoriteToggle?.();
                      onFavoriteClickWhenLoggedOut?.();
                    }}
                    className={`mt-px flex items-center gap-1 ${onFavoriteToggle || onFavoriteClickWhenLoggedOut ? 'hover:opacity-60' : ''}`}
                    aria-label={isLiked ? 'いいね解除' : 'いいね'}
                    data-testid='favorite-button'
                  >
                    <HeartIcon
                      fillColor={isLiked ? '#DD2B5C' : '#5F6368'}
                      filled={isLiked}
                      width={14}
                      height={14}
                    />
                    <span
                      className={`-mt-px text-[10px] ${isLiked ? 'text-like' : 'text-gray-400'}`}
                    >
                      {formatLikesCount(likesCount)}
                    </span>
                  </button>
                  {!isMobile && popupRef && (
                    <FavoriteLoginPopup
                      isVisible={isFavoritePopupVisible}
                      popupRef={popupRef}
                      onClose={onPopupClose}
                    />
                  )}
                </div>
                <div className='relative mt-px'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onBookmarkToggle) {
                        onBookmarkToggle();
                      }
                      if (onBookmarkClickWhenLoggedOut) {
                        onBookmarkClickWhenLoggedOut();
                      }
                    }}
                    className={`transition-opacity ${onBookmarkToggle || onBookmarkClickWhenLoggedOut ? 'hover:opacity-60' : ''}`}
                    aria-label={isBookmarked ? 'ブックマーク解除' : 'ブックマーク'}
                    data-testid='bookmark-button'
                  >
                    <BookmarkIcon
                      fillColor={isBookmarked ? brandColors[500] : '#6B7280'}
                      filled={isBookmarked}
                      width={14}
                      height={14}
                    />
                  </button>
                  {!isMobile && popupRef && (
                    <BookmarkLoginPopup
                      isVisible={isPopupVisible}
                      popupRef={popupRef}
                      onClose={onPopupClose}
                    />
                  )}
                </div>
              </div>
            </div>
            <Link
              to={to}
              className='block md:mt-3'
              data-testid='article-title-link'
            >
              <div className='flex items-start gap-1'>
                {isMemberLimited && (
                  <LockIcon
                    fillColor='#D4AF37'
                    className={`mt-1 size-[20px] shrink-0 ${lockIconMdClassName}`}
                    role='img'
                    aria-label='会員限定'
                  />
                )}
                <h2 className='min-w-0 truncate text-xl font-semibold md:line-clamp-2 md:whitespace-normal md:text-sm'>
                  <span className='block truncate md:overflow-visible md:whitespace-normal'>
                    {title}
                  </span>
                </h2>
              </div>
            </Link>
          </div>
        </div>
      </div>
      {isMobile && popupRef && (
        <>
          <BookmarkLoginPopup
            isVisible={isPopupVisible}
            popupRef={popupRef}
            onClose={onPopupClose}
          />
          <FavoriteLoginPopup
            isVisible={isFavoritePopupVisible}
            popupRef={popupRef}
            onClose={onPopupClose}
          />
        </>
      )}
      <div className='pt-2'>
        <p className={`line-clamp-2 text-sm leading-7 ${hideMobileDescription && 'md:hidden'}`}>
          {stripHtmlTags(content)}
        </p>
        <ul className='flex flex-wrap gap-2 pt-1'>
          {tags.map((tag) => (
            <Tag
              key={tag.id}
              label={tag.name}
              to={generateUrlWithParams('/articles', {
                tag_slug: tag.slug || '',
              })}
            />
          ))}
        </ul>
      </div>
    </article>
  );
};

export default ArticleItem;
